import { useState, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Search, Receipt, Calendar } from 'lucide-react';
import { useOrderStore } from '../../hooks';
import { Card, CardContent } from '@lancar-pos/ui';
import { formatCurrency } from '@lancar-pos/shared';
import { format, startOfDay, endOfDay, subDays, isWithinInterval } from 'date-fns';
import { id } from 'date-fns/locale';

type DateFilter = 'today' | 'week' | 'month' | 'all';

export function OrdersPage() {
  const navigate = useNavigate();
  const { orders } = useOrderStore();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState<DateFilter>('today');

  const filteredOrders = useMemo(() => {
    let filtered = orders.filter((o) => o.status !== 'draft');

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (o) =>
          o.orderNumber.toLowerCase().includes(query) ||
          o.items.some((item) => item.productName.toLowerCase().includes(query))
      );
    }

    const now = new Date();
    filtered = filtered.filter((o) => {
      const orderDate = new Date(o.createdAt);
      
      switch (dateFilter) {
        case 'today':
          return isWithinInterval(orderDate, {
            start: startOfDay(now),
            end: endOfDay(now),
          });
        case 'week':
          return isWithinInterval(orderDate, {
            start: startOfDay(subDays(now, 7)),
            end: endOfDay(now),
          });
        case 'month':
          return isWithinInterval(orderDate, {
            start: startOfDay(subDays(now, 30)),
            end: endOfDay(now),
          });
        default:
          return true;
      }
    });

    return filtered;
  }, [orders, searchQuery, dateFilter]);

  const totalRevenue = useMemo(() => {
    return filteredOrders
      .filter((o) => o.status === 'completed')
      .reduce((sum, o) => sum + o.total, 0);
  }, [filteredOrders]);

  const orderCount = filteredOrders.length;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">Selesai</span>;
      case 'cancelled':
        return <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full">Dibatalkan</span>;
      case 'pending':
        return <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-700 rounded-full">Menunggu</span>;
      default:
        return <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">{status}</span>;
    }
  };

  const getPaymentMethodBadge = (method: string) => {
    switch (method) {
      case 'cash':
        return <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Tunai</span>;
      case 'card':
        return <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">Kartu</span>;
      case 'qris':
        return <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded">QRIS</span>;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate({ to: '/app' })}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">Riwayat Pesanan</h1>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 lg:p-6 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-gray-500">Total Pesanan</p>
              <p className="text-2xl font-bold text-gray-900">{orderCount}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-gray-500">Total Pendapatan</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(totalRevenue)}</p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardContent className="p-4 space-y-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Cari nomor pesanan atau produk..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div className="flex gap-2">
                {(['today', 'week', 'month', 'all'] as DateFilter[]).map((filter) => (
                  <button
                    key={filter}
                    onClick={() => setDateFilter(filter)}
                    className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      dateFilter === filter
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {filter === 'today' && 'Hari Ini'}
                    {filter === 'week' && 'Minggu Ini'}
                    {filter === 'month' && 'Bulan Ini'}
                    {filter === 'all' && 'Semua'}
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      No. Pesanan
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Tanggal
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Items
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Total
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Pembayaran
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Status
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Aksi
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredOrders.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                        <Receipt className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                        <p>Belum ada pesanan</p>
                        <p className="text-sm text-gray-400">
                          {searchQuery ? 'Coba kata kunci lain' : 'Pesanan akan muncul di sini'}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    filteredOrders.map((order) => (
                      <tr key={order.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <span className="font-medium text-gray-900">{order.orderNumber}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {format(new Date(order.createdAt), 'dd MMM yyyy, HH:mm', { locale: id })}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {order.items.length} item
                          <span className="text-gray-400 ml-1">
                            ({order.items.reduce((sum, item) => sum + item.quantity, 0)} pcs)
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-gray-900">
                          {formatCurrency(order.total)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {getPaymentMethodBadge(order.paymentMethod)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {getStatusBadge(order.status)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center">
                            <button
                              onClick={() =>
                                navigate({ to: '/app/inventory/orders/$orderId', params: { orderId: order.id } })
                              }
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Lihat Detail"
                            >
                              <Receipt className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
